import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Download, X } from "lucide-react";

/**
 * Generic admin filter bar.
 * `fields` is an array of:
 *   { key, label, type: "text"|"select"|"date", options?: [{value,label}] }
 *
 * `value` is a plain object keyed by field.key.
 * `onChange(newValue)` is called on every keystroke / change.
 * `onExport()` is called when the export button is clicked.
 */
export default function FilterBar({
    fields,
    value,
    onChange,
    onExport,
    resultCount = 0,
    totalCount = 0,
    exportLabel = "Export CSV",
}) {
    const [open, setOpen] = useState(false);

    function update(k, v) {
        onChange({ ...value, [k]: v });
    }

    function clearAll() {
        const cleared = Object.fromEntries(fields.map((f) => [f.key, ""]));
        onChange(cleared);
    }

    const hasActive = Object.values(value || {}).some((v) => v && v !== "");

    return (
        <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-[#ECE4D3] bg-white p-4" data-testid="filter-bar">
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0E2A47]/40" />
                    <Input
                        value={value.q || ""}
                        onChange={(e) => update("q", e.target.value)}
                        placeholder="Search name, email, phone, reference…"
                        className="h-10 rounded-xl pl-9"
                        data-testid="filter-search"
                    />
                </div>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => setOpen((o) => !o)}
                    className="h-10 rounded-xl"
                    data-testid="filter-toggle"
                >
                    {open ? "Hide filters" : "More filters"}
                </Button>
                {hasActive && (
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={clearAll}
                        className="h-10 rounded-xl text-[#0E2A47]/60 hover:text-[#0E2A47]"
                        data-testid="filter-clear"
                    >
                        <X size={14} className="mr-1" /> Clear
                    </Button>
                )}
                <Button
                    type="button"
                    onClick={onExport}
                    className="h-10 rounded-xl bg-[#F26B1F] text-white hover:bg-[#D95A15]"
                    data-testid="filter-export"
                >
                    <Download size={14} className="mr-1.5" /> {exportLabel}
                </Button>
            </div>

            {open && (
                <div className="grid gap-3 border-t border-[#ECE4D3] pt-3 md:grid-cols-3 lg:grid-cols-4">
                    {fields
                        .filter((f) => f.key !== "q")
                        .map((f) => (
                            <div key={f.key}>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-[#0E2A47]/60">
                                    {f.label}
                                </label>
                                {f.type === "select" ? (
                                    <select
                                        value={value[f.key] || ""}
                                        onChange={(e) => update(f.key, e.target.value)}
                                        className="h-10 w-full rounded-xl border border-[#ECE4D3] bg-white px-3 text-sm"
                                        data-testid={`filter-${f.key}`}
                                    >
                                        <option value="">All</option>
                                        {(f.options || []).map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <Input
                                        type={f.type || "text"}
                                        value={value[f.key] || ""}
                                        onChange={(e) => update(f.key, e.target.value)}
                                        placeholder={f.placeholder || ""}
                                        className="h-10 rounded-xl"
                                        data-testid={`filter-${f.key}`}
                                    />
                                )}
                            </div>
                        ))}
                </div>
            )}

            <p className="text-xs text-[#475569]" data-testid="filter-result-count">
                Showing <span className="font-semibold text-[#0E2A47]">{resultCount}</span> of {totalCount}.
            </p>
        </div>
    );
}
